/**
 * Constantes propias del ER v2.
 *
 * El v2 EXTIENDE las del v1 en vez de modificarlas: el archivo equivalente del ER v1
 * (`ER/constants.js`) lo consumen 12+ call sites de su propio `index.js` y `CostosFijosTable`,
 * y el v1 está CONGELADO. Agregarle un concepto ahí le sumaría una fila al v1 — cambio de
 * render en la vista vieja, exactamente lo que la hermandad v1/v2 evita.
 *
 * FASE 1a (2026-10-01, independización del v2 — `docs/decisiones/2026-10-01-reingenieria-ebitda-fase1a.md`):
 * las constantes de abajo (hasta "FIN de las constantes copiadas del v1") son copia LITERAL,
 * sin ningún cambio, del equivalente en el ER v1 — antes se importaban de ahí
 * (`import { camposCostosFijos, nombresCostosFijos } from "../ER/constants"`), lo que dejaba al
 * v2 dependiendo del v1 para su propio motor de costos. Cero fixes acá: ni `camposCostosFijos`/
 * `nombresCostosFijos` cambiaron, ni se tocó `interesesHoldingPorPrestamo`/`COUNTRY_CONFIG`. El
 * ER v1 sigue intacto y es quien rige Perú.
 */

// === CONSTANTES COPIADAS DEL ER v1 (`ER/constants.js`, Fase 1a, idénticas) ===

export const meses = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

export const mesesCompletos = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export const camposCostosFijos = [
  "oficina",
  "transporte",
  "telefonia",
  "marketing",
  "contador",
  "abogados",
  "gastos_representacion",
  "asesoria_externa",
  "alojamiento",
  "viajes_gerencia",
  "gastos_bancarios",
  "varios",
];

export const nombresCostosFijos = [
  "Oficina",
  "Transporte",
  "Telefonía",
  "Marketing",
  "Contador",
  "Abogados",
  "Gastos representación",
  "Asesoría externa",
  "Alojamiento",
  "Viajes Gerencia",
  "Gastos Financieros",
  "Varios",
];

// Configuración centralizada por país (locale, moneda, URLs de dólar) — usada por
// `./hooks/useERFormatting.js` (copia Fase 1a, mismo import relativo `../constants`).
export const COUNTRY_CONFIG = {
  Chile: {
    locale: "es-CL",
    moneda: "$ ",
    dolarUrl: "https://wecast.cl/services/dolar/dolar.json",
    dolarLocalPath: "dolarchile",
  },
  Perú: {
    locale: "es-PE",
    moneda: "S/ ",
    dolarUrl: "https://visiona.pe/services/dolar/dolar.json",
    dolarLocalPath: "dolarperu",
  },
};

// Intereses del holding (en USD/mes) — VALOR PROVISORIO puesto a mano hasta el
// módulo de pasivos (cuotas de interés/capital). La deuda del holding se reparte
// 50/50 entre Chile y Perú: cada país paga la mitad de cada préstamo.
// Fuente única de verdad: el desglose por préstamo con el total del holding.
// De acá se derivan tanto la fila del ER como el InteresesModal (no duplicar literales).
const PROPORCION_PAIS = 0.5; // Chile y Perú pagan mitad y mitad

// Total holding por préstamo (USD/mes), antes del reparto por país. Se exporta para el
// desglose del InteresesModal en modo región (Chile+Perú = el total holding por préstamo).
export const interesesHoldingPorPrestamo = [
  { nombre: "Connolly", totalHolding: 1676 },
  { nombre: "Otegui", totalHolding: 212 },
];

// Desglose por préstamo del monto que paga CADA país (50% del total holding).
// Connolly 838, Otegui 106 → Total 944. Lo consume el InteresesModal.
export const interesesDesglosePorPais = interesesHoldingPorPrestamo.map((p) => ({
  nombre: p.nombre,
  montoPais: Math.round(p.totalHolding * PROPORCION_PAIS),
}));

// Total mensual por país (USD) = suma del desglose. Se conserva exactamente la
// forma { Chile, Perú } que consumen los cálculos del ER para no romper nada.
const interesMensualPorPais = interesesDesglosePorPais.reduce((sum, p) => sum + p.montoPais, 0);

export const interesesMensualesUSD = {
  Chile: interesMensualPorPais, // 944
  Perú: interesMensualPorPais, // 944
};

// Impuestos mensuales por país (en USD) - actualmente 0
export const impuestosMensualesUSD = {
  Chile: 0,
  Perú: 0,
};

/**
 * Reordena 2 arrays de 12 elementos en una ventana rodante de los últimos 12 meses.
 * Ej: monthsToShow=1 (Feb 2026) → [prev[1], prev[2], ..., prev[11], curr[0]]
 * Ej: monthsToShow=7 (Ago 2026) → [prev[7], prev[8], ..., prev[11], curr[0], ..., curr[6]]
 */
export function windowArray(current, previous, monthsToShow) {
  const result = [];
  for (let j = 0; j < 12 - monthsToShow; j++) {
    result.push(previous[monthsToShow + j] || 0);
  }
  for (let j = 0; j < monthsToShow; j++) {
    result.push(current[j] || 0);
  }
  return result;
}

// === FIN de las constantes copiadas del v1 ===

/**
 * Costos fijos del v2 = los 12 del v1 + Servicios Online.
 *
 * `servicios_online` es una `cuenta_er` que ya se venía clasificando (Google, Nubox y
 * similares) y que NO tenía línea destino en el ER: las facturas quedaban clasificadas,
 * fuera de toda línea y sumando en silencio al total sin clasificar, que además no se
 * renderiza. Costo real que se fugaba del EBITDA.
 *
 * Los dos arrays se indexan EN PARALELO (campo[i] ↔ nombre[i]) y varios consumidores del v2
 * dependen de esa alineación (`getCostoFijoValor` via indexOf, `costosFijosFilas`,
 * `costosFijosPorConcepto`). Agregar un concepto = agregarlo en AMBOS, al final.
 *
 * Pre-2026 la línea vale 0 por construcción: no hay campo homónimo en las tablas manuales,
 * así que `serieManualFija` no encuentra dato ni histórico para promediar y devuelve 0. La
 * paridad diff-cero contra el v1 en 2025 no se toca.
 */
export const camposCostosFijosV2 = [...camposCostosFijos, "servicios_online"];
export const nombresCostosFijosV2 = [...nombresCostosFijos, "Servicios Online"];

/**
 * Nota contable de la línea "Intereses" (debajo del EBITDA) — fundamento normativo de por qué
 * el costo total de factoring (interés + comisión del factor + mora) va completo ahí. Texto
 * único; se muestra como `title` (tooltip nativo, mismo patrón que TipoCambioTable.js) sobre
 * el label de la fila en EBITDAResultadoTable. Ver docs/modulos/er-v2.md y regla 10.277.
 */
export const NOTA_INTERESES_FACTORING =
  "Incluye el costo total del factoring: interés, comisión del factor y mora. El factoring de " +
  "ADIGITAL es con recurso, por lo que el anticipo es financiamiento y no venta de cartera. " +
  "Según NIC 23.5 y 23.6(a) y NIIF 9 B5.4.1, las comisiones de originación integran el costo " +
  "del financiamiento y se presentan junto al interés, debajo del EBITDA. No se identificó " +
  "componente de servicio (administración o cobranza) que corresponda a gasto operativo según " +
  "NIIF 9 B5.4.3. De identificarse en los contratos de los factores, esa porción se " +
  "reclasificaría sobre el EBITDA.";

/**
 * Definición escrita del EBITDA (PASO 7, punto 3 — Fase 5/3.11 del plan original de la
 * reingeniería). Texto único, mismo patrón que `NOTA_INTERESES_FACTORING` (tooltip nativo
 * `title`, sin dependencia nueva) sobre el ícono junto al label "EBITDA" en
 * `EBITDAResultadoTable.js`. Detalle con reglas/archivos: `docs/modulos/er-v2.md`.
 */
export const NOTA_DEFINICION_EBITDA =
  "EBITDA = Facturación − Costos variables − Costos fijos. Facturación: Chile desde 2026, " +
  "facturas emitidas al SII vinculadas a una venta, netas de IVA, con notas de crédito según su " +
  "vínculo; antes de 2026 y Perú, ventas por fecha de factura. Costos: facturas recibidas " +
  "clasificadas por cuenta, sueldos devengados y comisiones de las ventas facturadas. No " +
  "incluye depreciación, amortización, intereses (incluido factoring) ni impuestos. Las compras " +
  "de activos (CAPEX) no son costo: se deprecian. Montos en pesos; cada documento en dólares se " +
  "convierte una sola vez con el tipo de cambio de su fecha (o los pesos de su factura). Medida " +
  "definida por la administración (NIIF 18).";
